// PDF rasterization via pdf.js — converts multi-page PDFs into images for OCR.
// Exposes: window.OcrPdf = { renderPages(file, {scale, onProgress}) }
(function () {
  'use strict';

  const PDFJS_URL = '/vendor/pdfjs/4/pdf.min.mjs';
  const WORKER_URL = '/vendor/pdfjs/4/pdf.worker.min.mjs';

  let pdfjsLib = null;

  async function loadPdfJs() {
    if (pdfjsLib) return pdfjsLib;
    pdfjsLib = await import(PDFJS_URL);
    pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_URL;
    return pdfjsLib;
  }

  // Page count only — cheap (no rendering). Used to offer a page-range picker
  // before committing to rasterizing a 600-page book.
  async function getPageCount(file) {
    const lib = await loadPdfJs();
    const buf = await file.arrayBuffer();
    const doc = await lib.getDocument({ data: buf }).promise;
    const n = doc.numPages;
    try { doc.destroy(); } catch (e) {}
    return n;
  }

  // Render pages of a PDF file; returns { pages, failedPages, total }.
  // opts.from/opts.to (1-based, inclusive) select a range — the Neufert case:
  // OCRing pages 12–40 of a 600-page scan instead of everything.
  // Each page renders inside its own try/catch: one corrupt page no longer
  // rejects the whole document (err_pdf_pages partial-success path).
  //
  // STREAMING: when opts.onPage(pageObj) is provided, each rendered page is
  // awaited through the consumer and its canvas released before the next page
  // renders — peak memory stays at ~1 page instead of the whole document
  // (60 × ~8 MB canvases ≈ 0.5 GB pre-fix). In that mode `pages` returns
  // metadata only ({pageNumber, width, height}), no canvases.
  async function renderPages(file, opts) {
    opts = opts || {};
    const scale = opts.scale || 2.0; // 2x = ~150 DPI — solid accuracy without blowing memory
    const onProgress = opts.onProgress || function () {};
    const onPage = opts.onPage || null;

    const lib = await loadPdfJs();
    const buf = await file.arrayBuffer();
    const doc = await lib.getDocument({ data: buf }).promise;
    const pages = [];
    const failedPages = [];
    const total = doc.numPages;
    const from = Math.max(1, opts.from || 1);
    const to = Math.min(total, opts.to || total);
    const span = Math.max(0, to - from + 1);

    for (let i = from; i <= to; i++) {
      onProgress(i - from, span, 'render');
      try {
        const page = await doc.getPage(i);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;
        const pageObj = { pageNumber: i, canvas, width: canvas.width, height: canvas.height, totalPages: span };
        if (onPage) {
          await onPage(pageObj);                       // consumer OCRs the page…
          canvas.width = canvas.height = 1;            // …then the canvas is released
          pages.push({ pageNumber: i, width: pageObj.width, height: pageObj.height });
        } else {
          pages.push(pageObj);
        }
      } catch (e) {
        // A consumer-thrown 'Cancelled' must abort the whole document, not get
        // recorded as a page failure.
        if (e && String(e.message || e) === 'Cancelled') { try { doc.destroy(); } catch (_) {} throw e; }
        failedPages.push(i);
      }
      // Give the UI a chance to paint
      await new Promise(r => setTimeout(r, 0));
    }
    onProgress(span, span, 'render');
    try { doc.destroy(); } catch (e) {}
    return { pages, failedPages, total };
  }

  async function isPdfFile(file) {
    if (!file) return false;
    if (file.type === 'application/pdf') return true;
    if (/\.pdf$/i.test(file.name || '')) return true;
    // Sniff %PDF-
    try {
      const head = await file.slice(0, 5).arrayBuffer();
      const b = new Uint8Array(head);
      return b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46 && b[4] === 0x2D;
    } catch (e) { return false; }
  }

  window.OcrPdf = { renderPages, isPdfFile, getPageCount };
})();
